/**
 * features/expenses/ExpenseEditor.tsx
 * Add or edit a business expense.
 */
import { useState } from 'react';
import { X, Wallet, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatPula, round2 } from '@/lib/money';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import type { Expense, ExpenseCategory, PaymentMethod } from '@/types';
import { addExpense, updateExpense, deleteExpense } from './expensesWrite';
import { CATEGORY_LABEL, CATEGORY_LIST, METHOD_LABEL, METHOD_LIST } from './expenseMeta';
import { ReceiptCapture } from '@/features/common/ReceiptCapture';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function ExpenseEditor({ expense, onClose }: { expense: Expense | null; onClose: () => void }) {
  const { user } = useAuth();
  const [date, setDate] = useState(expense?.date ?? todayISO());
  const [category, setCategory] = useState<ExpenseCategory>(expense?.category ?? 'software');
  const [payee, setPayee] = useState(expense?.payee ?? '');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [method, setMethod] = useState<PaymentMethod>(expense?.method ?? 'bank_transfer');
  const [reference, setReference] = useState(expense?.reference ?? '');
  const [notes, setNotes] = useState(expense?.notes ?? '');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(expense?.attachmentUrl ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const amt = Number(amount) || 0;
  const valid = payee.trim().length > 0 && amt > 0;
  const actor = user?.email ?? user?.uid ?? 'unknown';

  async function save() {
    if (!valid) {
      setError('Payee and a positive amount are required.');
      return;
    }
    setBusy(true);
    setError('');
    const payload = {
      date,
      category,
      payee,
      description,
      amount: round2(amt),
      method,
      reference,
      notes,
      attachmentUrl: receiptUrl ?? undefined,
    };
    try {
      if (expense) await updateExpense(expense.id, payload);
      else await addExpense(payload, actor);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the expense.');
      setBusy(false);
    }
  }

  async function remove() {
    if (!expense) return;
    if (!window.confirm('Delete this expense?')) return;
    setBusy(true);
    try {
      await deleteExpense(expense.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete.');
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-10 w-full max-w-lg">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <Wallet size={18} />
              <h2 className="text-base font-semibold text-white">{expense ? 'Edit expense' : 'New expense'}</h2>
            </div>
            <button onClick={onClose} className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Category">
              <Select value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
                {CATEGORY_LIST.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Payee">
              <Input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Who was paid" />
            </Field>
            <Field label="Amount">
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="text-right" />
            </Field>
            <Field label="Method">
              <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHOD_LIST.map((m) => (
                  <option key={m} value={m}>
                    {METHOD_LABEL[m]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reference" hint="Txn / receipt no.">
              <Input value={reference} onChange={(e) => setReference(e.target.value)} />
            </Field>
          </div>

          <Field label="Description">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What it was for" />
          </Field>
          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          <Field label="Receipt" hint="Scan or upload for VAT records.">
            <ReceiptCapture value={receiptUrl ?? undefined} onChange={setReceiptUrl} folder="expenseReceipts" />
          </Field>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex items-center justify-between gap-2">
            <div>
              {expense && (
                <Button variant="ghost" icon={<Trash2 size={16} />} onClick={remove} disabled={busy}>
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={save} loading={busy} disabled={!valid}>
                Save {amt > 0 ? formatPula(round2(amt)) : 'expense'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
